// BLOG-008: WA Mining Sovereign Software post
import { ArrowRight } from "lucide-react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import PostHero from "../../components/PostHero";
import Reveal from "../../components/Reveal";

export default function WaMiningSovereign() {
  return (
    <div className="bg-black">
      <Nav route="inner" />

      <PostHero
        category="Insights"
        title="WA mining's next decade: AI, autonomy and the case for sovereign software"
        author="Paisums Editorial"
        date="January 2026"
        readTime="8 min read"
        excerpt="WA mining is at an inflection point. Operators investing in AI capability
         now — on their own data, under Australian data residency — will hold a
         structural advantage within three years. The ones waiting for the technology
         to mature will find it is already owned by someone else."
      />

      <main>
        <div className="max-w-3xl mx-auto px-6 py-12">

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              Where WA mining is heading
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The trajectory is clear. Autonomous haulage is no longer an
              experiment — it is operational at major Pilbara sites and the network
              effects are compounding. Computer vision is replacing manual quality
              inspection at processing plants. Predictive maintenance is cutting
              unplanned downtime across mid-tier operators in WA who could not
              previously justify the spend. The pattern repeats from the Pilbara to
              the Goldfields: AI moves from pilot to production wherever the data
              already exists.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The question is not whether AI will transform WA mining. It already
              is. The question is whether the next phase of that AI transformation
              is driven by sovereign software — built for Australian conditions,
              owned by Australian operators — or by offshore platforms that extract
              value from Australian operational data while optimising for a global
              average use case. How that question gets answered is the central
              theme of our work in <a href="/industries/mining.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI for mining in Western Australia</a>,
              and the decision belongs to operators, not vendors.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The sovereign software argument
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Sovereign software is not a nationalistic position. It is an
              operational one. Software built for WA mining conditions — 45 °C+ pit
              days, dust that defeats sensors, satellite links that drop, swing
              rosters that rotate your most experienced people off site every two
              weeks — performs better than software built for a global average. A
              system designed assuming fibre connectivity and a day-shift workforce
              degrades badly 1,300 km north of Perth.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The data sovereignty argument is just as practical. Mine planning
              data, equipment telemetry and ore-grade models are commercially
              sensitive, and the regulatory environment around critical minerals is
              tightening. Data residency in Australia is moving from a preference
              to a procurement requirement. The operators handling this well are
              not improvising — they put an AI governance baseline in place before
              the first system ships: a usage policy, data-handling rules, vendor
              assessment criteria. That groundwork is exactly what
              our <a href="/services/policy-generation.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">policy generation service</a> exists
              to deliver, and the takeaway is to do it before procurement starts,
              not after the first contract is signed.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What the leading operators are doing now
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The operations that will be in the strongest position in 2030 share a
              common approach: they build internal AI capability alongside external
              delivery. They do not outsource AI strategy entirely. They embed
              engineers who can maintain and extend AI systems after the initial
              build, and they treat their own operational data — every haul cycle,
              every failure event, every grade reconciliation — as the asset that
              trains the next generation of models.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              They also choose vendors on evidence. The consultancies and software
              providers they engage are selected on production experience in WA
              conditions, not on global case studies from unrelated sectors. A
              predictive maintenance system that performed well in a Norwegian oil
              field is not evidence of suitability for a Pilbara iron ore fleet.
              The takeaway for your next tender: ask every vendor which Australian
              site their system runs on today, and where the data is processed.
              Two questions, and most of the field disqualifies itself.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The mid-tier opportunity
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Most of the publicly visible AI investment in WA mining has been at
              the majors. The mid-tier — operations running between 50 and 500
              assets — has been slower to move, partly because the perceived cost
              barrier has been high and partly because enterprise platforms are
              priced and designed for fleets ten times that size.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              That barrier is lower than it was two years ago. The tooling has
              matured, the cost of taking a predictive maintenance system from
              prototype to production on a mid-tier fleet has dropped sharply, and
              the telemetry required is mostly already being collected. For
              operations at this scale, owning the system outright often beats
              renting seats on an offshore platform — the build-and-own model
              behind our <a href="/services/custom-products.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">custom AI products</a>.
              The decision takeaway: the capability gap between the majors and the
              mid-tier is closeable inside a 12 to 18 month investment cycle, and
              the operators who start now set the terms.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What to invest in now
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The highest-return near-term investments are data infrastructure and
              maintenance intelligence. Clean, continuous, well-documented telemetry
              is the foundation everything else depends on — and it has to be
              engineered for site reality, where satellite backhaul drops and data
              gaps are normal. Operations that fix data quality now will build
              faster and cheaper when they move to more advanced AI integration
              across planning, processing and logistics.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The second investment is people. An operation with one internal
              engineer who understands how its AI systems work — and can maintain
              them between vendor engagements — is structurally stronger than one
              that depends on an external party for every change. Embed that person
              in the first build, not after it.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The third is the delivery partner for the first production system. A
              well-delivered first system builds organisational confidence and
              internal capability. A poorly delivered one sets the AI programme
              back two to three years and makes the next investment harder to
              justify to the board. Scope small, ship to production, evidence the
              result — then scale. That sequencing, more than any technology
              choice, is what separates the WA operators who will own their AI
              capability in 2030 from the ones who will rent it.
            </p>
          </Reveal>

        </div>
      </main>

      <section className="bg-black px-6 pb-24 mt-4">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">
            Building sovereign AI capability for a WA operation?
          </h3>
          <p className="text-white/60 text-base mt-4">
            We build production AI systems for mining operators — engineered from
            Perth, governed under Australian data residency. Start with a
            30-minute conversation.
          </p>
          <a href="/#contact"
             className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3
                        bg-white text-black text-sm font-medium hover:bg-white/90
                        transition-colors">
            Talk to an engineer <ArrowRight size={16} />
          </a>
          <div>
            <a href="/industries/mining.html" className="mt-4 inline-flex items-center gap-2 liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors">AI for Mining &amp; Resources <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
